import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router';
import { differenceInMinutes } from 'date-fns';
import { translate } from '../../i18n';
import { 
  reservableUnitPatterMinutes, 
  yesNo, 
  useReasonCode as useReasonCodeValue, 
  purposeOfUse as purposeOfUseValue,
  lotteryAcceptTypeOption, 
  useReasonDisplayTypes
} from '../common/constant/classification';
import { facilityLotteryReservationFormUrl } from '../common/constant/appUrl';
import { OperationId } from '../common/constant/operationLog';
import { notifyMessage, showLoading } from '../common/store/slices/application';
import { selectSystemControl } from '../common/store/slices/systemControl';
import { selectLotteryReserveEntry, setLotteryReserve } from '../common/store/slices/lotteryReserve';
import { useErrorHandle } from '../common/error/errorHandler';
import { getFacility } from '../common/facility';
import { useOperationLog } from '../common/operationLog';
import { getUseReason } from '../common/useReason';
import { getUsageGuidStr } from '../common/reservationSelection';
import { 
  getLotteryReservation, 
  LotteryReservation, 
  IsCertificateUseReasonViewFlags
} from '../common/lotteryReservation';
import { executeGetChildren } from '../../dataAccess/webApi/dao/childrenDao';
import { executeGetFacilityReservationsWeekCitizen } from '../../dataAccess/webApi/dao/facilityReservationsWeekCitizenDao';
import { GetFacilityDto } from '../../dataAccess/webApi/dto/facilitiesDto';
import { GetFacilityReservationsWeekCitizenDto } from '../../dataAccess/webApi/dto/facilityReservationsWeekCitizenDto';
import { GetUseReasonDto } from '../../dataAccess/webApi/dto/useReasonDto';
import { 
  DateRange, 
  ElapsedMillisecond, 
  isValidDate, 
  toApiYmd, 
  formatHyphenYmd 
} from '../../utils/dateUtil';
import { promiseAllConcurrency } from '../../utils/promiseUtil';
import { fromNumber } from '../../utils/stringUtil';
import { NullPropsToUndefinedType } from '../../utils/typeUtil';
import { UseReasonOptions } from '../../views/components/facilityReservationForm/useReasonRadioGroup';
import { isUseReasonCategoryHidden } from '../common/reservation';

type UrlParams = {
  lotteryReservationNo: string
};
type DetailDescription = {
  value: string | null
}
type Inputs = {
  // 利用希望日時
  usageDesiredDatetimes: {
    value: {
      baseDateOfTime: Date // 時間の基準となる日付
      range: DateRange // 日時範囲
      status: string // 空き状況ステータス
    }
  }[],
  purposeOfUse: string | null
  useReasonDetailIrregular: string | null
  useReasonDetailEmg: string | null
  useReasonDetailRefresh: string | null
  useReasonDetailDescriptionsIrregular: DetailDescription[]
  useReasonDetailDescriptionsEmg: DetailDescription[]
  useReasonDetailDescriptionsRefresh: DetailDescription[]
};
type LocationState = {
  isKeep?: boolean // 入力済み情報から復元を試みる場合true
};
type CommonFlag = typeof yesNo[keyof typeof yesNo]
type PageState = {
  prevReservation: LotteryReservation // 変更前情報
  selectedChildId: string
  selectedChildName: string
  facility: NullPropsToUndefinedType<GetFacilityDto> // 施設情報
  activeDate: Date
  purposeOfUse?: string | null
  useReasonDetailCategory: string | null
  useReasonDetailDescription: string | null
  commonFlag: CommonFlag
  useReasonOptions: UseReasonOptions
  isCertificateUseReasonViewFlags: IsCertificateUseReasonViewFlags,
  usageGuideStr: string
  useReason: NullPropsToUndefinedType<GetUseReasonDto> // 利用目的マスタ情報
  availabilities: GetFacilityReservationsWeekCitizenDto[] //  空き状況（１週間分）
  lotteryNote: string | null
};
type ShowMessage = {
  Date: Date;
  message: string;
};

// 施設情報同時取得最大値
const MAX_WORKER_COUNT = 4;

export const useAction = () => {
  const errorHandle = useErrorHandle();
  const dispatch = useDispatch();
  const history = useHistory<LocationState | undefined>();
  const lotteryReservationNo = useParams<UrlParams>().lotteryReservationNo;
  const { addOperationLog, attachAccessData } = useOperationLog();
  const lotteryReserveEntry = useSelector(selectLotteryReserveEntry);
  const isKeep = history.location.state?.isKeep?? false;
  const sysCtrl = useSelector(selectSystemControl);
  const initialized = useRef(false);
  
  // ページ情報
  const [state, setState] = useState<PageState>();

  // フォーム関連
  const methods = useForm<Inputs>();
  const purposeOfUse = useWatch({ name: 'purposeOfUse', control: methods.control});
  const useReasonDetailIrregular = useWatch({ name: 'useReasonDetailIrregular', control: methods.control });
  const useReasonDetailEmg = useWatch({ name: 'useReasonDetailEmg', control: methods.control });
  const useReasonDetailRefresh = useWatch({ name: 'useReasonDetailRefresh', control: methods.control });
  const { append: appendUseReasonDetailDescriptionsIrregular } = useFieldArray({
    name: 'useReasonDetailDescriptionsIrregular',
    control: methods.control,
  });
  const { append: appendUseReasonDetailDescriptionsEmg } = useFieldArray({
    name: 'useReasonDetailDescriptionsEmg',
    control: methods.control,
  });
  const { append: appendUseReasonDetailDescriptionsRefresh } = useFieldArray({
    name: 'useReasonDetailDescriptionsRefresh',
    control: methods.control,
  });

  // 変更前の日時
  const previousRange = useMemo(() => {
    if (!state) return; 
    return {
      range: {
        from: new Date(state.prevReservation.useFromDatetime),
        to: new Date(state.prevReservation.useToDatetime),
      },
      baseDateOfTime: new Date(state.prevReservation.usageDate),
    }
  }, [state?.prevReservation]);

  // 「予約期間外」の文言表示
  const notifyDateFetchErrorMessage = useCallback(() => dispatch(notifyMessage(translate('facilityReservationSelection.error.noReservationReceptionSetting'))),[]);

  const getNotes = useCallback((facility: NullPropsToUndefinedType<GetFacilityDto>, purposeOfUse: string | null | undefined) => {
    if ( purposeOfUse === undefined || !facility.lotteryNotes ) return null;

    if ( facility.lotteryAcceptType === lotteryAcceptTypeOption.common ) {
      // 共通設定
      return facility.lotteryNotes[0].note;
    }

    // 利用目的別設定
    return facility.lotteryNotes.find((setting) => setting.useReasonCode === purposeOfUse)?.note ?? null;
  }, []);

  // 初期化
  useEffect(() => {
    addOperationLog({ operationId: OperationId.OP_00000001 });

    dispatch(
      showLoading(
        errorHandle(async () => {
          const [prevReservation, useReason] = await Promise.all([
            getLotteryReservation(lotteryReservationNo),
            getUseReason()
          ]);
          
          let activeDate = new Date(prevReservation.usageDate);
          let useFromDatetime = prevReservation.useFromDatetime;
          let useToDatetime = prevReservation.useToDatetime;
          let purposeOfUse: string | null = prevReservation.useReasonCategory;
          let useReasonDetailIrregular: string | null = null;
          let useReasonDetailEmg: string | null = null;
          let useReasonDetailRefresh: string | null = null;
          let useReasonDetailCategory = prevReservation.useReasonDetailCategory;
          let useReasonDetailDescription = prevReservation.useReasonDetailDescription;
          let isCertificateUseReasonViewFlags: IsCertificateUseReasonViewFlags = {irregularFlag: true, emgFlag: true, refreshFlag: true};
          const selectedChildId = prevReservation.childInfoList[0].childId;
          const useReasonDetailDescriptionsIrregular: DetailDescription[] = Array(useReason.detailsIrregular.length).fill(null).map(() => ({ value: null }));
          const useReasonDetailDescriptionsEmg: DetailDescription[] = Array(useReason.detailsEmg.length).fill(null).map(() => ({ value: null }));
          const useReasonDetailDescriptionsRefresh: DetailDescription[] = Array(useReason.detailsRefresh.length).fill(null).map(() => ({ value: null }));

          if (isKeep && lotteryReserveEntry) {
            // 次画面から戻ってきた場合
            const usageDatetime = lotteryReserveEntry.usageDatetimes[0]
            activeDate = new Date(usageDatetime.usageDate);
            useFromDatetime = usageDatetime.useFromDatetime;
            useToDatetime = usageDatetime.useToDatetime;
            purposeOfUse = lotteryReserveEntry.useReasonCategory;
            useReasonDetailCategory = lotteryReserveEntry.useReasonDetailCategory;
            useReasonDetailDescription = lotteryReserveEntry.useReasonDetailDescription;
            isCertificateUseReasonViewFlags = lotteryReserveEntry.isCertificateUseReasonViewFlags;
          }

          attachAccessData({
            accessData: [
              {
                userIdRegFlag: yesNo.yes,
                childId: selectedChildId,
                usageDate: toApiYmd(prevReservation.usageDate),
                reservationNo: prevReservation.reservationNo,                  
              }
            ],
          });
          
          const [child, facility, availabilities] = await Promise.all([
            getChildData(prevReservation.facilityId, selectedChildId),
            getFacility(prevReservation.facilityId, activeDate),
            getAvaiabilitiesForWeek(
              prevReservation.facilityId,
              selectedChildId,
              activeDate,
              purposeOfUse,
              lotteryReservationNo,
            )
          ]);

          // 抽選予約を受け付けているか
          const acceptLottery =  (
            sysCtrl.lotteryDisplayFlag === yesNo.yes 
            && sysCtrl.lotteryFlag === yesNo.yes 
            && facility.lotteryFlag === yesNo.yes 
          )

          if (!child || !facility || !availabilities || !acceptLottery) {
            // データ取得できない場合は「予約対象外」というエラー文言を表示
            notifyDateFetchErrorMessage();
            return;
          }

          switch (purposeOfUse) {
            case purposeOfUseValue.irregular:
              useReasonDetailIrregular = useReasonDetailCategory
              if (useReasonDetailIrregular) {
                const index = Number(useReasonDetailIrregular) - 1
                useReasonDetailDescriptionsIrregular[index].value = useReasonDetailDescription
              }
              
              break;
            case purposeOfUseValue.emergency:
              useReasonDetailEmg = useReasonDetailCategory
              if (useReasonDetailEmg) {
                const index = Number(useReasonDetailEmg) - 1
                useReasonDetailDescriptionsEmg[index].value = useReasonDetailDescription
              }
              break;
            case purposeOfUseValue.refresh:
              useReasonDetailRefresh = useReasonDetailCategory
              if (useReasonDetailRefresh) {
                const index = Number(useReasonDetailRefresh) - 1
                useReasonDetailDescriptionsRefresh[index].value = useReasonDetailDescription
              }
              break;
            default:
              break;
          }
          appendUseReasonDetailDescriptionsIrregular(useReasonDetailDescriptionsIrregular);
          appendUseReasonDetailDescriptionsEmg(useReasonDetailDescriptionsEmg);
          appendUseReasonDetailDescriptionsRefresh(useReasonDetailDescriptionsRefresh);

          if (!isKeep || !lotteryReserveEntry) {
            isCertificateUseReasonViewFlags = {irregularFlag: true, emgFlag: true, refreshFlag: true};
            if (facility.certificateFlag === yesNo.yes) {
              if(facility.certificateControlAtypical === yesNo.yes && child.useFlagAtypical === yesNo.no) {
                isCertificateUseReasonViewFlags.irregularFlag = false;
              }
              if(facility.certificateControlEmergency === yesNo.yes && child.useFlagEmergency === yesNo.no) {
                isCertificateUseReasonViewFlags.emgFlag = false;
              }
              if(facility.certificateControlRefresh === yesNo.yes && child.useFlagRefresh === yesNo.no) {
                isCertificateUseReasonViewFlags.refreshFlag = false;
              }
            } 
          }

          // 各利用目的の利用可否
          const useReasonOptions: UseReasonOptions = {
            irregular:
              facility.irregularDisplayType === useReasonDisplayTypes.usedInBoth
              && facility.useReasonUsageFlagIrregular === yesNo.yes
              && isCertificateUseReasonViewFlags.irregularFlag,
            emergency:
              facility.emergencyDisplayType === useReasonDisplayTypes.usedInBoth
              && facility.useReasonUsageFlagEmg === yesNo.yes
              && isCertificateUseReasonViewFlags.emgFlag,
            refresh:
              facility.refreshDisplayType === useReasonDisplayTypes.usedInBoth
              && facility.useReasonUsageFlagRefresh === yesNo.yes
              && isCertificateUseReasonViewFlags.refreshFlag,
          };

          // 共通OR利用目的別設定かの判断
          let commonFlag: CommonFlag = yesNo.no;
          if (
            facility.detailSetting.length === 1 
            && facility.detailSetting[0].useReasonCode === useReasonCodeValue.common 
            && facility.lotteryAcceptType === lotteryAcceptTypeOption.common
          ) {
            commonFlag = yesNo.yes;
            if (Object.values(useReasonOptions).every((value)=> !value)) {
              // 全ての利用目的が使用されない場合
              purposeOfUse = null;
            }
          }
          
          // カレンダー説明文
          const usageGuideStr = getUsageGuidStr(
            facility.immediatelyReservationFlag,
            facility.cancelWaitAcceptFlag,
            facility.postponeCancelWaitAcceptFlag,
            acceptLottery
          );

          methods.reset({
            usageDesiredDatetimes: [
              {
                value: {
                  baseDateOfTime: new Date(activeDate),
                  range: {
                    from: new Date(useFromDatetime),
                    to: new Date(useToDatetime),
                  },
                },
              },
            ],
            purposeOfUse,
            useReasonDetailIrregular,
            useReasonDetailEmg,
            useReasonDetailRefresh,
            useReasonDetailDescriptionsIrregular,
            useReasonDetailDescriptionsEmg,
            useReasonDetailDescriptionsRefresh
          });

          setState((prev) => ({
            ...prev,
            prevReservation,
            selectedChildId,
            selectedChildName: child.name,
            facility,
            activeDate,
            purposeOfUse,
            useReasonDetailCategory,
            useReasonDetailDescription,
            commonFlag,
            useReasonOptions,
            isCertificateUseReasonViewFlags,
            usageGuideStr,
            availabilities: availabilities,
            useReason,
            lotteryNote: getNotes(facility, purposeOfUse)
          }))
        })
      )
    );
  }, []);
  
  // 初期化＋コンテンツが表示後に一番上にスクロールする
  useEffect(() => {
    if (!state || initialized.current) return
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 0);
    initialized.current = true;
  }, [state])
  
  // 条件変更時に空き状況確認する
  useEffect(() => {
    if (!state) return;
    if (state.commonFlag === null) return;
      
    dispatch(
      showLoading({
        process: errorHandle(async () => {
          const [
            availabilities,
            facility,
          ] = await Promise.all([
            getAvaiabilitiesForWeek(
              state.prevReservation.facilityId,
              state.selectedChildId,
              state.activeDate,
              state.purposeOfUse,
              lotteryReservationNo,
            ),
            getFacility(state.prevReservation.facilityId, state.activeDate),
          ])
          
          if (availabilities) {
            setState((prev) => (
              { 
                ...prev as PageState,
                availabilities,
                facility,
              })
            );
          }else {
            // 異常処理
            notifyDateFetchErrorMessage();
          }
        }),
        isHiddenMain: false,
      })
    );
      
  }, [
      state?.selectedChildId,
      state?.activeDate,
      state?.purposeOfUse
    ]
  );
  
  // 日付変更
  const changeDate = useCallback((date: Date | null) => {
    if (!state || !date) return;
    addOperationLog({ operationId: OperationId.OP_00000029 });

    if (!isValidDate(date)) {
      notifyDateFetchErrorMessage();
      return;
    }

    setState((prev) => ({
      ...prev as PageState,
      activeDate: date
    }))
  }, [state]);

  // 利用目的変更
  const onChangeUseReason = useCallback((newPurpose: string) => {
    if (
      state?.commonFlag === undefined ||
      state?.facility === undefined
    ) return
    addOperationLog({ operationId: OperationId.OP_00000173 });

    const selectedUseReason = newPurpose;
    methods.reset({
      ...methods.getValues(),
      purposeOfUse: selectedUseReason,
    });

    if (state.commonFlag === yesNo.yes) return;

    setState((prev) => ({
      ...prev as PageState,
      purposeOfUse: selectedUseReason,
      lotteryNote: getNotes(state.facility, selectedUseReason)
    }));
  }, [
    state?.commonFlag,
    state?.facility,
  ])

  // 妥当性検査
  const validate = useCallback(async (data: Inputs) => {
    const response: {isError: boolean; showMessages: ShowMessage[]} = {
      isError: false,
      showMessages: []
    } 
    if (!state) return response;
    
    const facilityMap = await getFacilityMapByUsageDateElapsed(
      state.prevReservation.facilityId,
      new Set(data.usageDesiredDatetimes.map((v) => v.value.baseDateOfTime.getTime()))
    )

    // 対象日を取得する
    const entriesArray = Array.from(data.usageDesiredDatetimes.entries())

    for (const [idx, { value: { baseDateOfTime, range } }] of entriesArray) {
      const facility = facilityMap.get(baseDateOfTime.getTime())
      const minReservationMinute = facility?.minReservationMinute
      const reservableUnitPatternMinute = facility && reservableUnitPatterMinutes[facility?.reservableUnitPattern]
      if (minReservationMinute != null) {
        // 最低予約時間が設定されている場合
        const minute = differenceInMinutes(range.to, range.from)
        if (minute < minReservationMinute) {
          methods.setError(`usageDesiredDatetimes.${idx}.value`, {
            message: translate(
              'facilityReservationSelection.error.minReservationMinute',
              fromNumber(minReservationMinute)
            ),
          })
          response.isError = true
        }
      }
      if (reservableUnitPatternMinute != null) {
        // 予約可能単位が設定されている場合
        const minute = differenceInMinutes(range.to, range.from)
        if (minute % reservableUnitPatternMinute !== 0) {
          methods.setError(`usageDesiredDatetimes.${idx}.value`, {
            message: translate(
              'facilityReservationSelection.error.reservableUnitPattern',
              fromNumber(reservableUnitPatternMinute)
            ),
          })
          response.isError = true
        }
      }
    }

    return response
  }, [state]);

  // 次ページ移動ボタン押下時
  const onSubmit = useCallback((data: Inputs) => {
    addOperationLog({ operationId: OperationId.OP_00000030 });

    if (!state) return;
    
    dispatch(
      showLoading({
        process: errorHandle(async () => {
          // 妥当性検査
          const isValidateError = await validate(data);
          if (isValidateError.isError) {
            return
          }
          
          let useReasonDetailCategory: string | null = null
          let useReasonDetailDescription: string | null = null
    
          switch (data.purposeOfUse) {
            case purposeOfUseValue.irregular:
              useReasonDetailCategory = data.useReasonDetailIrregular
              if (useReasonDetailCategory){
                const indexOfDescription = Number(useReasonDetailCategory) - 1
                useReasonDetailDescription = data.useReasonDetailDescriptionsIrregular[indexOfDescription].value
              }
              break
            case purposeOfUseValue.emergency:
              useReasonDetailCategory = data.useReasonDetailEmg
              if (useReasonDetailCategory){
                const indexOfDescription = Number(useReasonDetailCategory) - 1
                useReasonDetailDescription = data.useReasonDetailDescriptionsEmg[indexOfDescription].value
              }
              break
            case purposeOfUseValue.refresh:
              useReasonDetailCategory = data.useReasonDetailRefresh
              if (useReasonDetailCategory){
                const indexOfDescription = Number(useReasonDetailCategory) - 1
                useReasonDetailDescription = data.useReasonDetailDescriptionsRefresh[indexOfDescription].value
              }
              break
          }

          // Redux上に現状データを保管
          dispatch(
            setLotteryReserve({
              reservationNo: lotteryReservationNo,
              updateDatetime: state.prevReservation.updateDatetime,
              useReasonCategory: data.purposeOfUse,
              useReasonDetailCategory,
              useReasonDetailDescription,
              isUseReasonCategoryHidden: isUseReasonCategoryHidden(data.purposeOfUse, state.facility),
              usageDatetimes: data.usageDesiredDatetimes
                .map((v) => ({
                  usageDate: v.value.baseDateOfTime.getTime(),
                  useFromDatetime: v.value.range.from.getTime(),
                  useToDatetime: v.value.range.to.getTime(),
                  usageBaseDate: formatHyphenYmd(v.value.baseDateOfTime),
                  status: v.value.status,
                }))
                .sort((a, b) => a.useFromDatetime - b.useFromDatetime),
              facility: {
                facilityId: state.prevReservation.facilityId,
                ...state.facility
              },
              childInfoList: state.prevReservation.childInfoList,
              commonInput: {
                lunchFlag: state.prevReservation.lunchFlag,
                snackFlag: state.prevReservation.snackFlag,
                postponeCancelWaitFlag: state.prevReservation.postponeCancelWaitFlag,
                goingTimes: [
                  ... ( 
                    isUsageDatetimeUpdated(
                      state.prevReservation.useFromDatetime,
                      state.prevReservation.useToDatetime,
                      data.usageDesiredDatetimes[0].value.range.from.getTime(),
                      data.usageDesiredDatetimes[0].value.range.to.getTime()
                    )
                    ? []
                    : [
                        state.prevReservation.goingTime
                      ]
                  )
                ],
                formUseReason: state.prevReservation.formUseReason,
                preferenceProvisionCertificateFlag: state.prevReservation.preferenceProvisionCertificateFlag,
                citizenNote: state.prevReservation.citizenNote,
                consentCheck: ''
              },
              useReason: state.useReason,
              isCertificateUseReasonViewFlags: state.isCertificateUseReasonViewFlags,
            })
          );

          // 戻るで表示した際に取得・入力済み情報から復元を試みる為に履歴に保管
          history.replace({
            ...history.location,
            state: {
              isKeep: true,
            },
          });

          // 次ページへ移動
          history.push(facilityLotteryReservationFormUrl.url())
        }),
        isHiddenMain: false,
      })
    )
  }, [state, validate, addOperationLog]);

  return {
    state,
    previousRange,
    methods,
    purposeOfUse,
    useReasonDetailIrregular,
    useReasonDetailEmg,
    useReasonDetailRefresh,
    onSubmit,
    changeDate,
    onChangeUseReason,
  }
}

const getChildData =  async(facilityId: string, childId: string) => {
  const response = await executeGetChildren({ facilityId });
  return response.result.find((data) => data.childId === childId);
}

const getAvaiabilitiesForWeek = async (
  facilityId: string,
  selectedChildId: string,
  targetDate: Date,
  useReasonCode: string | null | undefined,
  lotteryReservationNo: string | null
) => {
  const response = await executeGetFacilityReservationsWeekCitizen(facilityId, {
    childId: selectedChildId,
    targetDate: toApiYmd(targetDate),
    useReasonCode: useReasonCode?? purposeOfUseValue.common,
    reservationNo: lotteryReservationNo,
  });
  return response.result;
}

const getFacilityMapByUsageDateElapsed = async (facilityId: string, usageDateElapsedSet: Set<ElapsedMillisecond>) => {
  const facilities = await promiseAllConcurrency(
    Array.from(usageDateElapsedSet).map((usageDateElapsed) => async () => {
      const facility = await getFacility(facilityId, new Date(usageDateElapsed))
      return [usageDateElapsed, facility] as const
    }),
    MAX_WORKER_COUNT
  )
  return new Map(facilities)
}

/**
 * 予約変更を行っている予約の利用日時に更新がされているか確認する
 * @param prevUseFromDatetime 
 * @param prevUseToDatetime 
 * @param currUseFromDatetime 
 * @param currUseToDatetime 
 * @returns 
 */
const isUsageDatetimeUpdated = (
  prevUseFromDatetime: number,
  prevUseToDatetime: number,
  currUseFromDatetime: number,
  currUseToDatetime: number
) => {
  return (
    prevUseFromDatetime !== currUseFromDatetime
    || prevUseToDatetime !== currUseToDatetime
  );
}

